/* eslint-disable eqeqeq */
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { DynamicGuardService } from '@workflow/dynamic-guard/dynamic-guard.decorator';
import { RouterUrlConstant } from '../constant/information.const';
import { CanComponentDeactivate } from '../model/can-component-deactivate.type';
import { StorageWrapperService } from '../service/storage-wrapper/storage-wrapper.service';
import { UserService } from '../service/user/user.service';
import { StatefulService } from '@app/service/stateful.service';

@DynamicGuardService('PreventApplicatinBackGuard')
@Injectable({ providedIn: 'root' })
export class PreventApplicatinBackGuard {
  constructor(
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly statefulService: StatefulService,
    private readonly storageWrapperService: StorageWrapperService
  ) {}

  /** MORTGAGE FLOW ID */
  public canDeactivate(
    component: CanComponentDeactivate,
    route: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.isAllowedPath(nextState.url);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot, next: ActivatedRouteSnapshot): boolean {
    return this.isAllowedPath(state.url, route);
  }

  private isAllowedPath(url: string, route: ActivatedRouteSnapshot = undefined): boolean {
    const user = this.userService.user;
    const userToken = user && user.token ? user.token : undefined;
    const loanType = this.statefulService.wrap(`api:loan/loanInformation/loanType`).valueString;
    const isAuthenticated = this.userService.isAuthenticated;
    const auth = this.statefulService.wrap('ss:authorization').valueString;
    const submittedLoan = this.statefulService.wrap(`api:submitToLOSResult/submissionStatus`).valueString;
    const isSubmitted = submittedLoan && submittedLoan.toLowerCase() === 'postapplication' ? true : false;

    if (!isAuthenticated && !this.isOLAFlow && !this.isOAOFlow && !this.isSHeloc && url.includes('user/light-registration')) {
      return this.isRestrictRouteForUnLoggedUser(userToken, url, route);
    }

    if (!isAuthenticated || !auth) {
      return true;
    }
    if (isAuthenticated && url.indexOf('user/light-registration') > -1) {
      this.logout();
      return false;
    } else if (isAuthenticated && this.isRestrictRouteForLoginUser(url, loanType, isSubmitted)) {
      this.statefulService.wrap('ss:stay-Dashboard-Page').value = true;
      this.router.navigateByUrl(`/consumer/dashboard?return=dashboard`);
      return false;
    }
    return true;
  }

  private get isOAOFlow(): boolean {
    const membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;
    const membershipId = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeID').valueString;
    return membershipTypeName || membershipId ? true : false;
  }

  private get isOLAFlow(): boolean {
    const loanGroup = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').valueString;
    return loanGroup ? true : false;
  }

  private get isSHeloc(): boolean {
    return this.statefulService.wrap('api:isStreamLinedHeloc').value as boolean;
  }

  private isRestrictRouteForLoginUser(url: string, loanType: string, isSubmitted: boolean): boolean {
    this.storageWrapperService.set('nextStateUrl', '');
    if (isSubmitted) {
      return true;
    }
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  private isRestrictRouteForUnLoggedUser(userToken: any, url: string, route: ActivatedRouteSnapshot = undefined): boolean {
    url = this.cleanPath(url);
    const isUserNotAuthenticated: boolean = !userToken && url !== RouterUrlConstant.Login && url !== RouterUrlConstant.Econsent;
    const isApplicationRoutingPages: boolean = url.includes('eligibility');
    const isLightRegistrationRoutingPages: boolean =
      url == RouterUrlConstant.RegistrationLight && this.statefulService.wrap('api:loanParticipant/isAssociated').value == null;

    if (isUserNotAuthenticated && (isApplicationRoutingPages || isLightRegistrationRoutingPages)) {
      this.router.navigate([RouterUrlConstant.Login]);
      return false;
    }
    return true;
  }
  private logout(navigateTologin = false) {
    this.userService.logout();
    if (navigateTologin) {
      this.router.navigate(['/']);
    }
  }

  private cleanPath(path: string): string {
    return path.includes('?') ? path.split('?')[0] : path;
  }
}
